<template>
  <form class="flex flex-col w-full sm:pt-4 pb-8 px-0 bg-white" @submit.prevent="signUp">
    <h2 class="text-2xl sm:text-3xl font-black text-slate-900 mb-6 leading-tight text-right md:text-center sm:text-right">קדימה, שנרשום אותך?</h2>

    <div class="space-y-5">
      <!-- Full Name -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-bold text-slate-700" for="fullName">שם מלא</label>
        <IconField>
          <InputText
            id="fullName"
            v-model="fullName"
            type="text"
            placeholder="הקלידי את שמך..."
            class="pr-10 w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all"/>
          <InputIcon class="pi pi-user !text-slate-400"/>
        </IconField>
        <small v-show="fullNameError !== ''" class="text-red-500 text-xs mt-1 font-medium">{{ fullNameError }}</small>
      </div>

      <!-- Phone Number -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-bold text-slate-700" for="phoneNumber">מספר טלפון</label>
        <IconField>
          <InputMask
            id="phoneNumber"
            v-model="phoneNumber"
            inputmode="numeric"
            mask="999-999-9999"
            pattern="[0-9]*"
            slotChar=" "
            placeholder="050-000-0000"
            class="pr-10 w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all text-right"
            dir="ltr"
          />
          <InputIcon class="pi pi-phone !text-slate-400"/>
        </IconField>
        <small v-show="phoneNumberError !== ''" class="text-red-500 text-xs mt-1 font-medium">{{ phoneNumberError }}</small>
      </div>

      <!-- Email -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-bold text-slate-700" for="email">כתובת אימייל</label>
        <IconField>
          <input-text
            id="email"
            v-model="email"
            type="email"
            :disabled="preEmail"
            placeholder="mail@example.com"
            class="pr-10 w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all text-right disabled:opacity-60"
            dir="ltr"
          />
          <InputIcon class="pi pi-at !text-slate-400"/>
        </IconField>
        <small v-show="emailError !== ''" class="text-red-500 text-xs mt-1 font-medium">{{ emailError }}</small>
      </div>

      <!-- Password -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-bold text-slate-700" for="password">סיסמה</label>
        <div class="relative">
          <IconField>
            <input-text
              id="password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="הקלידו סיסמה חזקה..."
              class="pr-10 pl-10 w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all text-right"
              dir="rtl"
            />
            <InputIcon class="pi pi-lock !text-slate-400"/>
          </IconField>
          <button
            type="button"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            @click="showPassword = !showPassword"
          >
            <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" class="text-sm" />
          </button>
        </div>
        <small v-show="passwordError !== ''" class="text-red-500 text-xs mt-1 font-medium">{{ passwordError }}</small>
      </div>

      <!-- Referrer -->
      <div v-if="!preEmail && !referralCode" class="flex flex-col gap-2 mt-2">
        <label class="text-sm font-bold text-slate-700" for="referer">איך שמעת עלינו?</label>
        <Select
          v-model="referer"
          :options="referrers"
          optionLabel="name"
          optionValue="value"
          placeholder="בחרי תשובה"
          class="w-full !bg-slate-50 !border-slate-200 !rounded-xl"
          :pt="{ root: { class: '!border-slate-200 focus:!border-pink-500' } }"
        />
      </div>

      <div v-if="referer === 'other' || referer === 'friend'" class="flex flex-col gap-2 transition-all">
        <label class="text-sm font-bold text-slate-700" for="refererOptions">נשמח לעוד קצת פרטים ☺️</label>
        <input-text
          id="refererOptions"
          v-model="refererOptions"
          type="text"
          placeholder="כתבי לנו כאן..."
          class="w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all"
        />
      </div>
    </div>

    <!-- Terms -->
    <p class="text-xs mt-8 text-slate-500 leading-relaxed text-center sm:text-right">
      ע״י לחיצה על כפתור ההרשמה או הרשמה באמצעות גוגל/אפל אני מאשר/ת את
      <a class="font-bold text-pink-600 hover:text-pink-700 underline transition-colors" :href="termsOfUseLink" target="_blank">תנאי השימוש</a>
      וקבלת תוכן שיווקי.
    </p>

    <!-- Submit Button -->
    <Button
      v-wave
      label="מעבר לשלב הבא"
      :loading="loading"
      class="w-full flex-none mt-6 !bg-gradient-to-r !from-pink-500 !to-purple-600 hover:!from-pink-400 hover:!to-purple-500 !text-white !font-bold !py-4 !rounded-xl !border-0 transition-all !shadow-lg shadow-pink-500/30 transform hover:-translate-y-1 flex items-center justify-center text-lg"
      @click="signUp"
    />

    <Divider
      v-if="!preEmail"
      type="solid"
      align="center"
      class="!my-8"
      layout="horizontal"
    >
      <span class="text-slate-400 text-sm bg-white px-4">או הרשמי באמצעות</span>
    </Divider>

    <social-login-buttons v-if="!preEmail" :referral-code="referralCode" class="w-full" />

    <!-- Mobile Login Link -->
    <div v-if="isMobile && !preEmail" class="w-full flex justify-center items-center mt-12 pb-4">
      <div class="px-5 py-3 flex gap-2 rounded-xl bg-slate-50 border border-slate-100 text-sm shadow-sm">
        <span class="text-slate-600">יש לך כבר משתמש?</span>
        <router-link class="text-pink-600 font-bold hover:underline" :to="{ name: AuthRouteNames.AdminLogin }">
          התחברות למערכת
        </router-link>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import {useToast} from "vue-toastification";
import {useRoute, useRouter} from "vue-router";
import validatePhone from "phone";
import EmailPassword from "supertokens-web-js/recipe/emailpassword";
import {AuthRouteNames} from "@app/router/route-names";
import {ref} from "vue";
import {useResponsive} from "@app/composables/responsive";
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import InputMask from 'primevue/inputmask';
import Button from 'primevue/button';
import Divider from 'primevue/divider';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import SocialLoginButtons from "@app/components/auth/SocialLoginButtons.vue";
import Session from "supertokens-web-js/recipe/session";
import { trackEvent } from '@app/composables/posthog';

const toast = useToast();
const router = useRouter();
const route = useRoute();
const {isMobile} = useResponsive();

const referralCode = route.query['referral-code'] as string;
const termsOfUseLink = `${import.meta.env.VITE_BUSINESS_WEBSITE_DOMAIN}/terms-of-use`;
const referrers = [
  {name: 'פייסבוק / אינסטגרם', value: 'meta'},
  {name: 'גוגל', value: 'google'},
  {name: 'חבר/ה', value: 'friend'},
  {name: 'אחר', value: 'other'},
]
const loading = ref(false);
const fullName = ref('');
const phoneNumber = ref('');
const email = ref('');
const password = ref('');
const showPassword = ref(false);
const referer = ref('');
const refererOptions = ref('');

const preEmail = ref(false);

const fullNameError = ref('');
const phoneNumberError = ref('');
const emailError = ref('');
const passwordError = ref('');

const clearFields = (): void => {
  fullNameError.value = '';
  phoneNumberError.value = '';
  passwordError.value = '';
  emailError.value = '';
};

const signUp = async (_: Event) => {
  trackEvent('register_clicked');
  loading.value = true;

  clearFields();

  let hasError = false;
  if (fullName.value === '' || fullName.value.length < 2) {
    fullNameError.value = 'צריך להיות לפחות 2 תווים';
    hasError = true;
  }

  const phoneToSend = `${phoneNumber.value.substring(1)}`;
  if (
    phoneNumber.value === '' ||
    !validatePhone(phoneToSend, {country: 'IL'}).isValid
  ) {
    phoneNumberError.value = 'מספר הטלפון שהוזן לא תקין';
    hasError = true;
  }
  if (email.value === '' || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.value)) {
    emailError.value = 'כתובת מייל לא תקינה';
    hasError = true;
  }
  if (password.value === '' ||
    password.value.length < 8 ||
    !/.*[0-9].*/.test(password.value) ||
    !/(.*[a-z].*)/.test(password.value)
  ) {
    passwordError.value = 'הסיסמא לא תקינה, על הסיסמא להכיל לפחות 8 תווים, אות ומספר';
    hasError = true;
  }
  if (hasError) {
    loading.value = false;
    return;
  }

  try {
    const response = await EmailPassword.signUp({
      formFields: [
        {id: 'email', value: email.value},
        {id: 'password', value: password.value},
        {id: 'phoneNumber', value: `0${phoneToSend.split('-').join('')}`},
        {id: 'fullName', value: fullName.value},
        {id: 'referer', value: refererOptions.value ?? referer.value},
      ],
    });
    if (response.status === 'FIELD_ERROR') {
      trackEvent('register_failed', { reason: 'field_error' });
      loading.value = false;
      clearFields();
      response.formFields.forEach((item) => {
        if (item.id === 'email') {
          if (item.error.includes('email already exists'))
            toast.error('כתובת המייל הזו כבר קיימת במערכת');
          else toast.error('משהו לא תקין עם כתובת המייל הזו..');
        } else if (item.id === 'password')
          toast.error('משהו לא תקין בסיסמא שהזנת');
        else if (item.id === 'phoneNumber')
          toast.error('מספר הטלפון כבר קיים במערכת');
      });
      return;
    }
    trackEvent('register_succeeded', { referral: !!referralCode, referer: referer.value });
    loading.value = false;
    if (response.fetchResponse.status === 201)
      await router.replace({name: AuthRouteNames.AdminLogin});
    else {
      const accessTokenPayload = await Session.getAccessTokenPayloadSecurely();
      if (accessTokenPayload && accessTokenPayload.mainClient === false)
        await router.replace({name: AuthRouteNames.AdminLogin});
      else
        await router.replace({
          query: {step: 'business', ...route.query, 'referral-code': referralCode}
        });
    }
  } catch (e) {
    loading.value = false;
    console.error(e);
    toast.error('לצערנו ההרשמה נכשלה, אנא נסו שוב');
  }
};

if (route.query?.error) toast.error('נראה שמשהו נכשל.. אנא נסו שוב');

if (route.query?.email) {
  preEmail.value = true;
  email.value = (<string>route.query.email).toLowerCase().replace(' ', '+').trim();
}
if (route.query?.fullName) fullName.value = <string>route.query.fullName;
</script>
